import { createRng, type Rng } from '@jianghu/shared';
import type { ZoneDef } from '@jianghu/game-data';
import * as THREE from 'three';

function canvas(w: number, h: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d');
  if (!ctx) throw new Error('2D canvas unavailable');
  return [c, ctx];
}

function tex(c: HTMLCanvasElement, repeat = false): THREE.CanvasTexture {
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 4;
  if (repeat) {
    t.wrapS = THREE.RepeatWrapping;
    t.wrapT = THREE.RepeatWrapping;
  }
  return t;
}

function blobs(
  ctx: CanvasRenderingContext2D,
  rng: Rng,
  w: number,
  h: number,
  n: number,
  colors: string[],
  rMin: number,
  rMax: number,
  alpha: number,
): void {
  for (let i = 0; i < n; i++) {
    ctx.globalAlpha = alpha * (0.4 + rng.next() * 0.6);
    ctx.fillStyle = rng.pick(colors);
    ctx.beginPath();
    ctx.ellipse(
      rng.range(0, w),
      rng.range(0, h),
      rng.range(rMin, rMax),
      rng.range(rMin, rMax) * rng.range(0.5, 1),
      rng.range(0, Math.PI),
      0,
      Math.PI * 2,
    );
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

/**
 * Hand-painted-style ground splat map covering the whole terrain mesh: grass variation, meadow,
 * dirt and stone roads, plaza tiles, rice paddies, river bed and camp dirt, all derived from the
 * same zone data used for collision.
 */
export function groundTexture(zone: ZoneDef, extent: number, size: number): THREE.CanvasTexture {
  const [c, ctx] = canvas(size, size);
  const rng = createRng(zone.seed + 7);
  const s = size / (extent * 2);
  const px = (x: number): number => (x + extent) * s;
  const pz = (z: number): number => (z + extent) * s;

  ctx.fillStyle = '#5f7d3c';
  ctx.fillRect(0, 0, size, size);
  blobs(
    ctx,
    rng,
    size,
    size,
    2200,
    ['#6b8a43', '#56733a', '#7a9550', '#4f6b33', '#68844a'],
    size * 0.004,
    size * 0.02,
    0.35,
  );
  // Outside the play area: drier slopes.
  ctx.save();
  ctx.globalAlpha = 0.55;
  ctx.fillStyle = '#6e7650';
  const b = zone.bounds;
  ctx.beginPath();
  ctx.rect(0, 0, size, size);
  ctx.rect(px(b.minX - 6), pz(b.minZ - 6), (b.maxX - b.minX + 12) * s, (b.maxZ - b.minZ + 12) * s);
  ctx.fill('evenodd');
  ctx.restore();
  // Northern meadow: warmer, sun-bleached grass.
  ctx.save();
  const g = ctx.createRadialGradient(px(0), pz(-76), 0, px(0), pz(-76), 45 * s);
  g.addColorStop(0, 'rgba(160,170,90,0.45)');
  g.addColorStop(1, 'rgba(160,170,90,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  ctx.restore();
  // Flower specks.
  blobs(
    ctx,
    rng,
    size,
    size,
    1400,
    ['#d9c45a', '#e6e2d0', '#c97a8a', '#b9a4e0'],
    size * 0.0008,
    size * 0.0016,
    0.55,
  );

  // Rice paddies.
  for (const f of zone.fields) {
    ctx.fillStyle = '#6d8a52';
    ctx.fillRect(px(f.minX), pz(f.minZ), (f.maxX - f.minX) * s, (f.maxZ - f.minZ) * s);
    ctx.fillStyle = 'rgba(120,160,165,0.35)';
    for (let z = f.minZ; z < f.maxZ; z += 1.2)
      ctx.fillRect(px(f.minX), pz(z), (f.maxX - f.minX) * s, 0.45 * s);
    ctx.strokeStyle = '#7b6a46';
    ctx.lineWidth = 0.8 * s;
    for (let x = f.minX; x <= f.maxX; x += 9) {
      ctx.beginPath();
      ctx.moveTo(px(x), pz(f.minZ));
      ctx.lineTo(px(x), pz(f.maxZ));
      ctx.stroke();
    }
    ctx.strokeRect(px(f.minX), pz(f.minZ), (f.maxX - f.minX) * s, (f.maxZ - f.minZ) * s);
  }

  // Trampled earth around the bandit camp.
  const camp = zone.areas.find((a) => a.id === 'bandit_camp');
  if (camp) {
    const cg = ctx.createRadialGradient(px(camp.x), pz(camp.z), 0, px(camp.x), pz(camp.z), camp.r * s);
    cg.addColorStop(0, 'rgba(122,104,78,0.95)');
    cg.addColorStop(0.75, 'rgba(122,104,78,0.75)');
    cg.addColorStop(1, 'rgba(122,104,78,0)');
    ctx.fillStyle = cg;
    ctx.fillRect(0, 0, size, size);
  }

  // Dirt under buildings and around the village core.
  for (const st of zone.structures) {
    if (st.kind !== 'house' && st.kind !== 'hall' && st.kind !== 'watchtower' && st.kind !== 'tent') continue;
    ctx.save();
    ctx.translate(px(st.x), pz(st.z));
    ctx.rotate(-st.rot);
    ctx.fillStyle = 'rgba(110,96,70,0.6)';
    ctx.fillRect((-st.w / 2 - 1.5) * s, (-st.d / 2 - 1.5) * s, (st.w + 3) * s, (st.d + 3) * s);
    ctx.restore();
  }

  // River bed.
  for (const w of zone.water) {
    ctx.fillStyle = '#3b5a50';
    ctx.fillRect(px(w.x - w.hw), pz(w.z - w.hd - 0.6), w.hw * 2 * s, (w.hd * 2 + 1.2) * s);
  }
  for (const st of zone.structures) {
    if (st.kind === 'pond') {
      ctx.fillStyle = '#3b5a50';
      ctx.beginPath();
      ctx.arc(px(st.x), pz(st.z), (st.w / 2 + 0.6) * s, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  // Roads: soft dark edge, then core, then texture.
  for (const road of zone.roads) {
    const stroke = (width: number, style: string, alpha: number): void => {
      ctx.globalAlpha = alpha;
      ctx.strokeStyle = style;
      ctx.lineWidth = width * s;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      road.points.forEach(([x, z], i) => (i === 0 ? ctx.moveTo(px(x), pz(z)) : ctx.lineTo(px(x), pz(z))));
      ctx.stroke();
      ctx.globalAlpha = 1;
    };
    if (road.kind === 'stone') {
      stroke(road.width + 1.4, '#6f6a55', 0.5);
      stroke(road.width, '#8e8b80', 1);
    } else {
      stroke(road.width + 1.6, '#7d6a48', 0.45);
      stroke(road.width, '#a08a60', 1);
      stroke(road.width * 0.35, '#b39b70', 0.35);
    }
  }
  // Stone road and plaza paving pattern.
  ctx.save();
  ctx.beginPath();
  ctx.arc(px(zone.plaza.x), pz(zone.plaza.z), zone.plaza.r * s, 0, Math.PI * 2);
  ctx.fillStyle = '#918d82';
  ctx.fill();
  ctx.clip();
  ctx.strokeStyle = 'rgba(60,58,52,0.45)';
  ctx.lineWidth = Math.max(1, 0.08 * s);
  for (let r = 1.2; r < zone.plaza.r; r += 1.2) {
    ctx.beginPath();
    ctx.arc(px(zone.plaza.x), pz(zone.plaza.z), r * s, 0, Math.PI * 2);
    ctx.stroke();
  }
  for (let a = 0; a < Math.PI * 2; a += Math.PI / 12) {
    ctx.beginPath();
    ctx.moveTo(px(zone.plaza.x), pz(zone.plaza.z));
    ctx.lineTo(px(zone.plaza.x + Math.sin(a) * zone.plaza.r), pz(zone.plaza.z + Math.cos(a) * zone.plaza.r));
    ctx.stroke();
  }
  ctx.restore();
  blobs(ctx, rng, size, size, 500, ['rgba(40,40,30,0.25)'], size * 0.001, size * 0.003, 0.4);
  return tex(c);
}

/** Grey-blue glazed roof tiles (repeating). */
export function roofTileTexture(): THREE.CanvasTexture {
  const [c, ctx] = canvas(128, 128);
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, 128, 128);
  for (let x = 0; x < 128; x += 16) {
    const grd = ctx.createLinearGradient(x, 0, x + 16, 0);
    grd.addColorStop(0, '#9a9a9a');
    grd.addColorStop(0.35, '#f2f2f2');
    grd.addColorStop(0.7, '#d0d0d0');
    grd.addColorStop(1, '#7c7c7c');
    ctx.fillStyle = grd;
    ctx.fillRect(x, 0, 16, 128);
  }
  ctx.fillStyle = 'rgba(0,0,0,0.22)';
  for (let y = 0; y < 128; y += 21) ctx.fillRect(0, y, 128, 3);
  return tex(c, true);
}

/** Lacquered plaque with gold characters. */
export function plaqueTexture(text: string, bg = '#2a1f1a', fg = '#e8c873'): THREE.CanvasTexture {
  const [c, ctx] = canvas(256, 96);
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, 256, 96);
  ctx.strokeStyle = fg;
  ctx.lineWidth = 5;
  ctx.strokeRect(6, 6, 244, 84);
  ctx.fillStyle = fg;
  ctx.font = 'bold 54px "Noto Serif CJK SC", "Noto Serif CJK TC", "Songti SC", "SimSun", serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, 128, 52);
  return tex(c);
}

/** Taiji diagram inside a ring of the eight trigrams, used by the Taiji Formation VFX. */
export function taijiTexture(color = '#ffd77a'): THREE.CanvasTexture {
  const size = 512;
  const [c, ctx] = canvas(size, size);
  const m = size / 2;
  ctx.clearRect(0, 0, size, size);
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = 6;
  ctx.globalAlpha = 0.9;
  ctx.beginPath();
  ctx.arc(m, m, 240, 0, Math.PI * 2);
  ctx.stroke();
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.arc(m, m, 200, 0, Math.PI * 2);
  ctx.stroke();
  // Eight trigrams.
  const trigrams = [7, 3, 5, 1, 6, 2, 4, 0];
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    ctx.save();
    ctx.translate(m + Math.sin(a) * 220, m - Math.cos(a) * 220);
    ctx.rotate(a);
    for (let line = 0; line < 3; line++) {
      const solid = ((trigrams[i] as number) >> line) & 1;
      const y = -10 + line * 10;
      if (solid) ctx.fillRect(-22, y - 3, 44, 6);
      else {
        ctx.fillRect(-22, y - 3, 18, 6);
        ctx.fillRect(4, y - 3, 18, 6);
      }
    }
    ctx.restore();
  }
  // Yin-yang.
  ctx.globalAlpha = 0.85;
  const r = 140;
  ctx.beginPath();
  ctx.arc(m, m, r, -Math.PI / 2, Math.PI / 2, false);
  ctx.arc(m, m + r / 2, r / 2, Math.PI / 2, -Math.PI / 2, true);
  ctx.arc(m, m - r / 2, r / 2, Math.PI / 2, -Math.PI / 2, false);
  ctx.fill();
  ctx.lineWidth = 5;
  ctx.beginPath();
  ctx.arc(m, m, r, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(m, m - r / 2, r / 7, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(0,0,0,0)';
  ctx.globalCompositeOperation = 'destination-out';
  ctx.fill();
  ctx.globalCompositeOperation = 'source-over';
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(m, m + r / 2, r / 7, 0, Math.PI * 2);
  ctx.fill();
  const t = tex(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

/** Soft round sprite for particles and glows. */
export function glowTexture(): THREE.CanvasTexture {
  const [c, ctx] = canvas(64, 64);
  const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.25, 'rgba(255,255,255,0.75)');
  g.addColorStop(0.6, 'rgba(255,255,255,0.18)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 64, 64);
  return tex(c);
}

/** Tiling grey grain used to add close-up detail to the ground splat map. */
export function detailTexture(): THREE.CanvasTexture {
  const size = 256;
  const [c, ctx] = canvas(size, size);
  const rng = createRng(77);
  ctx.fillStyle = '#c8c8c8';
  ctx.fillRect(0, 0, size, size);
  for (let i = 0; i < 2600; i++) {
    const v = Math.floor(rng.range(120, 255));
    ctx.fillStyle = `rgba(${v},${v},${v},${rng.range(0.15, 0.5).toFixed(2)})`;
    const x = rng.range(0, size);
    const y = rng.range(0, size);
    const r = rng.range(0.6, 3.2);
    // Draw wrapped copies so the texture tiles seamlessly.
    for (const dx of [-size, 0, size]) {
      for (const dy of [-size, 0, size]) {
        ctx.beginPath();
        ctx.ellipse(x + dx, y + dy, r, r * rng.range(0.4, 1), rng.range(0, 3), 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }
  const t = new THREE.CanvasTexture(c);
  t.wrapS = THREE.RepeatWrapping;
  t.wrapT = THREE.RepeatWrapping;
  t.anisotropy = 4;
  return t;
}
